import React, { useEffect, useState } from 'react'
import {
    Grid,
    Box,
    Typography,
    Card
} from "@mui/material"
import * as actions from "../ComingSoon/actions"
import { toastError } from '../../utils'
import FronicLogo from '../../assets/img/Fronic_Logo_white.png'

// Ripple animation for live indicator
const rippleAnimation = `
    @keyframes pulse-dot {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.2); opacity: 0.7; }
    }

    @keyframes ripple {
        0% {
            transform: scale(0.8);
            opacity: 1;
        }
        100% {
            transform: scale(2.5);
            opacity: 0;
        }
    }
`

export default function LiveAnalytics() {
    const [analyticsData, setAnalyticsData] = useState({
        total_online_users: 0,
        online_users_list: [],
        pages: []
    })
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        fetchAnalytics()
        // Auto-refresh every 30 seconds
        const interval = setInterval(() => {
            fetchAnalytics()
        }, 30000)

        return () => clearInterval(interval)
    }, [])

    const fetchAnalytics = () => {
        setLoading(true)
        actions.getOnlineUsersAdmin()
            .then(res => {
                if (res.status === 200) {
                    setAnalyticsData(res.body)
                }
                setLoading(false)
            })
            .catch(err => {
                toastError(err.message || 'Failed to fetch analytics')
                setLoading(false)
            })
    }

    return (
        <>
            {/* Inject ripple animation */}
            <style>{rippleAnimation}</style>

            <Box style={{
                padding: '2vh 3vw',
                height: '100vh',
                width: '100vw',
                background: '#0a0a0a',
                color: '#fff',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Header - 12vh */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ height: '12vh', marginBottom: '2vh' }}
                >
                    <Box display="flex" alignItems="center" gap={2}>
                        <img
                            src={FronicLogo}
                            alt="Fronic Media"
                            style={{
                                height: '6vh',
                                width: 'auto',
                                objectFit: 'contain'
                            }}
                        />
                        <Typography style={{ fontSize: '3.5vh', fontWeight: 700, color: '#666', letterSpacing: '1px' }}>
                            Live Analytics
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Typography style={{ fontSize: '1.8vh', color: '#b0b0b0', fontWeight: 500 }}>
                            Updating live
                        </Typography>

                        {/* Red pulsing dot with ripple waves */}
                        <Box
                            style={{
                                position: 'relative',
                                width: '1.2vh',
                                height: '1.2vh',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {/* Ripple wave 1 */}
                            <Box
                                style={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    border: '0.15vh solid #f44336',
                                    animation: 'ripple 1.5s ease-out infinite',
                                    animationDelay: '0s'
                                }}
                            />
                            {/* Ripple wave 2 */}
                            <Box
                                style={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    border: '0.15vh solid #f44336',
                                    animation: 'ripple 1.5s ease-out infinite',
                                    animationDelay: '0.5s'
                                }}
                            />
                            {/* Ripple wave 3 */}
                            <Box
                                style={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    border: '0.15vh solid #f44336',
                                    animation: 'ripple 1.5s ease-out infinite',
                                    animationDelay: '1s'
                                }}
                            />
                            {/* Center red dot */}
                            <Box
                                style={{
                                    width: '1.2vh',
                                    height: '1.2vh',
                                    backgroundColor: '#f44336',
                                    borderRadius: '50%',
                                    animation: 'pulse-dot 1.5s ease-in-out infinite',
                                    boxShadow: '0 0 1vh rgba(244, 67, 54, 0.5)',
                                    zIndex: 1
                                }}
                            />
                        </Box>
                    </Box>
                </Box>

            {/* Main Stats - Users Online - Dynamic height */}
            <Card
                elevation={0}
                style={{
                    background: '#1a1a1a',
                    padding: '3vh 2vw',
                    textAlign: 'center',
                    borderRadius: '1.5vh',
                    minHeight: '22vh',
                    marginBottom: '2.5vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    border: '1px solid #2a2a2a',
                    position: 'relative'
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={3}>
                    {/* Left side users - horizontal wrap */}
                    <Box
                        style={{
                            flex: 1,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '1vh 1vw',
                            justifyContent: 'flex-start',
                            alignItems: 'center'
                        }}
                    >
                        {analyticsData.online_users_list
                            .slice(0, Math.ceil(analyticsData.online_users_list.length / 2))
                            .map((user, index) => {
                                const name = user.name || user.email || 'Anonymous'
                                const truncatedName = name.length > 22 ? name.substring(0, 22) + '...' : name
                                return (
                                    <Typography
                                        key={index}
                                        style={{
                                            fontSize: 'calc(1.4vh + 2px)',
                                            color: '#8b8b8b',
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        • {truncatedName}
                                    </Typography>
                                )
                            })}
                    </Box>

                    {/* Center count with border box */}
                    <Box
                        style={{
                            flex: '0 0 auto',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '2vh 3vw',
                            border: '2px solid #2a2a2a',
                            borderRadius: '1.5vh',
                            background: '#0f0f0f'
                        }}
                    >
                        <Typography style={{ fontSize: '1.8vh', color: '#b0b0b0', fontWeight: 600, marginBottom: '1vh', letterSpacing: '2px' }}>
                            USERS ONLINE NOW
                        </Typography>
                        <Typography
                            style={{
                                fontSize: '12vh',
                                color: '#10b981',
                                fontWeight: 900,
                                lineHeight: 1,
                                textShadow: '0 0 30px rgba(16, 185, 129, 0.3)'
                            }}
                        >
                            {analyticsData.total_online_users}
                        </Typography>
                    </Box>

                    {/* Right side users - horizontal wrap */}
                    <Box
                        style={{
                            flex: 1,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '1vh 1vw',
                            justifyContent: 'flex-end',
                            alignItems: 'center'
                        }}
                    >
                        {analyticsData.online_users_list
                            .slice(Math.ceil(analyticsData.online_users_list.length / 2))
                            .map((user, index) => {
                                const name = user.name || user.email || 'Anonymous'
                                const truncatedName = name.length > 22 ? name.substring(0, 22) + '...' : name
                                return (
                                    <Typography
                                        key={index}
                                        style={{
                                            fontSize: 'calc(1.4vh + 2px)',
                                            color: '#8b8b8b',
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {truncatedName} •
                                    </Typography>
                                )
                            })}
                    </Box>
                </Box>
            </Card>

            {/* Page Activity Section - Remaining vh */}
            <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Typography style={{ fontSize: '2vh', fontWeight: 600, color: '#b0b0b0', textAlign: 'center', marginBottom: '2vh', letterSpacing: '2px' }}>
                    PAGE ACTIVITY (per user)
                </Typography>
                {analyticsData.pages.length > 0 ? (
                    <Box
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            height: '100%',
                            overflow: 'hidden',
                            paddingRight: '1vw'
                        }}
                    >
                        <Box
                            style={{
                                display: 'grid',
                                gridTemplateColumns: (() => {
                                    const activePages = analyticsData.pages.filter(page => page.active_users_count > 0)
                                    const count = activePages.length
                                    // Calculate optimal columns based on card count
                                    if (count <= 5) return `repeat(${count}, minmax(0, 17vw))`
                                    if (count <= 10) return 'repeat(5, minmax(0, 17vw))'
                                    if (count <= 15) return 'repeat(5, minmax(0, 16vw))'
                                    if (count <= 24) return 'repeat(6, minmax(0, 14vw))'
                                    if (count <= 35) return 'repeat(7, minmax(0, 12vw))'
                                    if (count <= 48) return 'repeat(8, minmax(0, 11vw))'
                                    return 'repeat(9, minmax(0, 10vw))'
                                })(),
                                gap: (() => {
                                    const activePages = analyticsData.pages.filter(page => page.active_users_count > 0)
                                    const count = activePages.length
                                    if (count <= 10) return '2vh'
                                    if (count <= 24) return '1.5vh'
                                    if (count <= 35) return '1.2vh'
                                    return '1vh'
                                })(),
                                justifyContent: 'center',
                                width: '100%',
                                maxWidth: '100%',
                                alignContent: 'start'
                            }}
                        >
                        {analyticsData.pages
                            .filter(page => page.active_users_count > 0)
                            .sort((a, b) => b.active_users_count - a.active_users_count)
                            .map((page, index) => {
                                const activePages = analyticsData.pages.filter(p => p.active_users_count > 0)
                                const count = activePages.length

                                // Dynamic sizing based on card count
                                const getCardHeight = () => {
                                    if (count <= 5) return '18vh'
                                    if (count <= 10) return '16vh'
                                    if (count <= 15) return '14vh'
                                    if (count <= 24) return '12vh'
                                    if (count <= 35) return '10vh'
                                    if (count <= 48) return '8vh'
                                    return '7vh'
                                }

                                const getTitleFontSize = () => {
                                    if (count <= 10) return '1.6vh'
                                    if (count <= 24) return '1.4vh'
                                    if (count <= 35) return '1.2vh'
                                    if (count <= 48) return '1.1vh'
                                    return '1vh'
                                }

                                const getCountFontSize = () => {
                                    if (count <= 10) return '5vh'
                                    if (count <= 24) return '4vh'
                                    if (count <= 35) return '3.5vh'
                                    if (count <= 48) return '3vh'
                                    return '2.5vh'
                                }

                                const getLabelFontSize = () => {
                                    if (count <= 10) return '1.3vh'
                                    if (count <= 24) return '1.1vh'
                                    if (count <= 35) return '1vh'
                                    if (count <= 48) return '0.9vh'
                                    return '0.8vh'
                                }

                                return (
                                    <Card
                                        key={index}
                                        elevation={0}
                                        style={{
                                            background: '#1a1a1a',
                                            border: page.active_users_count > 0
                                                ? '2px solid #10b981'
                                                : '1px solid #2a2a2a',
                                            padding: count > 24 ? '1.5vh 1vw' : '2vh 1.5vw',
                                            borderRadius: '1vh',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            transition: 'all 0.3s ease',
                                            height: getCardHeight(),
                                            boxShadow: page.active_users_count > 0
                                                ? '0 0 20px rgba(16, 185, 129, 0.2)'
                                                : 'none'
                                        }}
                                    >
                                        <Typography
                                            style={{
                                                color: '#e0e0e0',
                                                fontWeight: 600,
                                                fontSize: getTitleFontSize(),
                                                marginBottom: count > 24 ? '0.5vh' : '1vh',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: count > 24 ? 1 : 2,
                                                WebkitBoxOrient: 'vertical',
                                                minHeight: count > 24 ? 'auto' : '3.2vh'
                                            }}
                                        >
                                            {page.page_name}
                                        </Typography>
                                        <Box textAlign="center">
                                            <Typography
                                                style={{
                                                    fontSize: getCountFontSize(),
                                                    color: page.active_users_count > 0 ? '#10b981' : '#666',
                                                    fontWeight: 900,
                                                    lineHeight: 1,
                                                    textShadow: page.active_users_count > 0
                                                        ? '0 0 20px rgba(16, 185, 129, 0.3)'
                                                        : 'none'
                                                }}
                                            >
                                                {page.active_users_count}
                                            </Typography>
                                        </Box>
                                    </Card>
                                )
                            })}
                        </Box>
                    </Box>
                ) : (
                    <Box textAlign="center" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography style={{ fontSize: '2.5vh', color: '#666' }}>
                            No page data available
                        </Typography>
                    </Box>
                )}
            </Box>

            </Box>
        </>
    )
}
